import * as React from 'react';
import { uniqBy } from "../../misc/ArrayFunctions";
import { useSecurityContext } from "../../Core/SecurityContext";

import { formatDateTime } from "../../misc/components/Formatters/Formatters";
import {
    Permission,
} from "../../misc/api/apiTypes";
import { SessionTypes } from '../../misc/api/ApiSessionTypes';
import { useCurrentStudyConfigurationContext } from '../../Core/CurrentStudyConfigurationContext';
import { Table, Column } from '../../Components/QueryTable';
import { DateMatchSortHeader } from '../../Components/DateMatchSortHeader';
import { MatchSortHeader } from '../../Components/MatchSortHeader';
import { FvcAnalysisSet, FvcSession, WebPortalClient, AnalysisSet, DlcoSession } from 'webportalclient';
import { SpirometryAnalysisSetTypeEnum } from '../../misc/api/ApiSpirometryAnalysisSetType';
import { useQuery, Filter } from '../../Components/Query';
import { DlcoAnalysisSetTypeEnum } from '../../misc/api/ApiDlcoAnalysisSetType';
import { AnalysisAcceptabilityStatus } from '../../misc/components/analysis/AnalysisAcceptabilityStatus';
import { SortHeader } from '../../Components/SortHeader';
import { PermissionEnum } from '../../misc/api/ApiPermissionEnum';
import { OverreadStatusHeader } from '../../Components/OverreadStatusHeader';
import { useReferenceContextProvider } from '../../Core/ReferenceContextProvider';
import { StatusHeader } from '../../Components/StatusHeader';
import { MatchSortCollectionHeader } from '../../Components/MatchSortCollectionHeader';
import { useWebPortalApiClient } from '../../misc/api/WebPortalTokenClient';
import { OverreaderHeaderText } from './FilterModals/OverreaderFilterModal';
import { OverreaderHeader } from '../../Components/OverreaderHeader';

interface PFTSessionListTableProps {
    header: string;
    sessionType: SessionTypes;
    hamburgerMenu: (spSessionMetdata: FvcSession | DlcoSession, currentPermissions: Permission[], currentStudyId: string) => JSX.Element;
}

export const PFTSessionListTable = ({ header, sessionType, hamburgerMenu }: PFTSessionListTableProps): JSX.Element => {
    const { data, applyFilters } = useQuery();
    const webPortalClient = useWebPortalApiClient();
    const { currentUserPermissions } = useSecurityContext();
    const { study } = useCurrentStudyConfigurationContext();
    const { overreadStatuses } = useReferenceContextProvider();
    const [siteOptions, setSiteOptions] = React.useState([]);
    const [subjectOptions, setSubjectOptions] = React.useState([]);
    const [visitOptions, setVisitOptions] = React.useState([]);
    const [sessionOptions, setSessionOptions] = React.useState([]);
    const [technicians, setTechnicians] = React.useState([]);
    const [overreaders, setOverreaders] = React.useState([]);
    const [overreadChallengeStatuses, setOverreadChallengeStatuses] = React.useState([]);

    const formatSpirometryOverreader = (session: FvcSession): string => {
        const overreadAnalysisSet = session.analysisSets.find(a => a.type === SpirometryAnalysisSetTypeEnum.Overread || a.type === DlcoAnalysisSetTypeEnum.Overread);

        return overreadAnalysisSet ? overreadAnalysisSet.user : "-";
    }

    React.useEffect(() => {
        const getSites = async () => {
            const sites = await webPortalClient.client.getSites("id,number", `study/id eq ${study.id}`, "number");
            const options = sites.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSiteOptions(options);
        };


        const getSubjects = async () => {
            const subjects = await webPortalClient.client.getSubjects("id,number", `site/study/id eq ${study.id}`, "number");
            const options = subjects.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSubjectOptions(options);
        };

        const getVisits = async () => {
            const visits = await webPortalClient.client.getStudyScheduleVisits("id,label", `studySchedule/studyId eq ${study.id}`, "label");

            const options = uniqBy(visits.value, (a, b) => a.label === b.label).map(s => ({
                value: s.id,
                label: s.label,
            }));

            setVisitOptions(options);
        };

        const getSessions = async () => {
            const scheduleSessions = await webPortalClient.client.getStudyScheduleSessions("id,label", `studyScheduleVisit/studySchedule/studyId eq ${study.id}`, "label");

            const sessions = await webPortalClient.client.getSessions("sessionType,sessionTypeName,technician", `visit/subject/site/studyId eq ${study.id}`);

            const options = uniqBy(scheduleSessions.value, (a, b) => a.label === b.label).map(s => ({
                value: s.label,
                label: s.label,
            }));

            setSessionOptions(options);

            const technicians = uniqBy(sessions.value, (a, b) => a.technician === b.technician).map(s => ({
                value: s.technician,
                label: s.technician,
            }));

            setTechnicians(technicians);
        };

        const getOverreadChallengeStatuses = async () => {
            const overreadChallengeStatuses = await webPortalClient.client.getOverreadChallengeStatuses();

            const options = overreadChallengeStatuses.value.filter(o => o.name).map(s => ({
                value: s.id,
                label: s.name,
            }));

            setOverreadChallengeStatuses(options);
        };

        const getOverreaders = async () => {
            const overreaderUsers = await webPortalClient.client.getPermissions("id,roles", `roles/any(r: r/studyId eq ${study.id} or r/name eq 'Administrator') and (id eq '${PermissionEnum.OverreadFvcSession}' or id eq '${PermissionEnum.OverreadDlcoSession}')`, undefined, "roles($select=studyId,users;$expand=users($select=id,email;$filter=isDisabled eq false))");
            const overreadersUnique = uniqBy(overreaderUsers.value.flatMap(x => x.roles).flatMap(x => x.users), (a, b) => a.email === b.email).map(s => ({
                value: s.email.replace(/'/g, "''"),
                label: s.email,
            }));

            setOverreaders(overreadersUnique);
        }

        getOverreaders();
        getSites();
        getSubjects();
        getVisits();
        getSessions();
        getOverreadChallengeStatuses();

    }, [location.search]);

    const clearAllFilters = () => {
        applyFilters([]);
    }

    const formatSiteStatus = (sets: AnalysisSet[]) => {
        const siteSet = sets.find(x => x.type === SpirometryAnalysisSetTypeEnum.Site
            || x.type === DlcoAnalysisSetTypeEnum.Site
        );

        return (<>
            <AnalysisAcceptabilityStatus value={siteSet ? siteSet?.status : null} />
        </>);
    }

    const formatOverreadingStatus = (sets: AnalysisSet[]) => {
        const overreadSet = sets.find(x => x.type === SpirometryAnalysisSetTypeEnum.Overread
            || x.type === DlcoAnalysisSetTypeEnum.Overread
        );

        if (!overreadSet)
            return "-";

        return (<>
            <AnalysisAcceptabilityStatus value={overreadSet.status} />
        </>);
    }

    const statusesOptions = overreadStatuses.map(os => ({
        value: os.id,
        label: os.name
    }));

    const viewOverreaderName = () => {
        return currentUserPermissions.some(x => (x.Id === PermissionEnum.ViewOverreaderName));
    }

    const overreadAnalysisSetType = () => {
        switch (sessionType) {
            case SessionTypes.Fvc:
                return SpirometryAnalysisSetTypeEnum.Overread;
            case SessionTypes.Dlco:
                return DlcoAnalysisSetTypeEnum.Overread;
        }
    }

    const siteAnalysisSetType = () => {
        switch (sessionType) {
            case SessionTypes.Fvc:
                return SpirometryAnalysisSetTypeEnum.Site;
            case SessionTypes.Dlco:
                return DlcoAnalysisSetTypeEnum.Site;
        }
    }

    return (
        <>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">{header}</h2>
                </div>
                <div className="col-md-6">
                    <div className="h2 pull-right">
                        <button className="btn btn-primary" onClick={clearAllFilters}>
                            Clear All Filters
                        </button>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div className="ibox">
                        <div className="ibox-content">
                            <div className="table-responsive">
                                <Table data={data}>
                                    <Column field="visit" header={<MatchSortHeader title="Site ID" field="visit/subject/siteId" options={siteOptions} sortField="visit/subject/site/number" />} formatter={visit => visit.subject.site.number} />
                                    <Column field="visit" header={<MatchSortHeader title="Subject ID" field="visit/subjectId" options={subjectOptions} sortField="visit/subject/number" />} formatter={visit => visit.subject.number} />
                                    <Column field="visit" header={<MatchSortHeader title="Visit" field="visit/visitScheduleItemId" options={visitOptions} sortField="visit/name" />} formatter={visit => visit.name} />
                                    <Column field="name" header={<MatchSortHeader title="Session" field="name" options={sessionOptions} sortField="name" />} formatter={name => name} />
                                    <Column field="occurred" header={<DateMatchSortHeader title="Session Start" field="occurred" sortField="occurred" />} formatter={occurred => formatDateTime(occurred)} />
                                    <Column field="tests@odata.count" header={<SortHeader title="Tests" field="testsCount" />} formatter={t => t} />
                                    <Column field="technician" header={<MatchSortHeader title="Technician" field="technician" options={technicians} sortField="technician" />} formatter={technician => technician} />
                                    <Column field="lastReceived" header={<DateMatchSortHeader title="Last Received" field="lastReceived" sortField="lastReceived" />} formatter={lastReceived => formatDateTime(lastReceived)} />
                                    <Column field="analysisSets"
                                        header={<OverreadStatusHeader title="Over-read Status" overreadAnalysisSetTypeValue={overreadAnalysisSetType()} orderField="status" overreadStatuses={statusesOptions} overreadChallengeStatuses={overreadChallengeStatuses} />}
                                        formatter={status => formatOverreadingStatus(status)}
                                    />
                                    {viewOverreaderName() ? <Column field="" header={<OverreaderHeader title="Over-reader" usernameField="user" automationId="Overreader" userOptions={overreaders} />} formatter={session => formatSpirometryOverreader(session)} /> : null}
                                    <Column field="" formatter={s => hamburgerMenu(s, currentUserPermissions, study.id)} />
                                </Table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
